import { baseApi } from "./baseApi";

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

export interface Coupon {
  code: string;
  discountType: "PERCENTAGE" | "FIXED";
  discountValue: number;
  minPurchase: number | null;
  maxDiscount: number | null;
  validFrom: string;
  validUntil: string;
  usageLimit: number | null;
  usedCount: number;
  usageLimitPerUser: number | null;
  isFirstOrderOnly: boolean;
  isFreeDelivery: boolean;
  isActive: boolean;
  applicableCategoryIds: string[] | null;
  applicableProductIds: string[] | null;
  createdAt: string;
  updatedAt: string;
}

export interface ValidateCouponRequest {
  code: string;
  items: Array<{
    productId: string;
    quantity: number;
    subtotal: number;
  }>;
  subtotal: number;
}

export const couponsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCoupons: builder.query<ApiResponse<Coupon[]>, void>({
      query: () => "/coupons",
      providesTags: ["Coupon"],
    }),
    getCouponByCode: builder.query<ApiResponse<Coupon>, string>({
      query: (code) => `/coupons/${code}`,
      providesTags: (_result, _error, code) => [{ type: "Coupon", id: code }],
    }),
    createCoupon: builder.mutation<ApiResponse<Coupon>, Partial<Coupon>>({
      query: (data) => ({
        url: "/coupons",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Coupon"],
    }),
    updateCoupon: builder.mutation<ApiResponse<Coupon>, { code: string; data: Partial<Coupon> }>({
      query: ({ code, data }) => ({
        url: `/coupons/${code}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { code }) => ["Coupon", { type: "Coupon", id: code }],
    }),
    deleteCoupon: builder.mutation<ApiResponse<void>, string>({
      query: (code) => ({
        url: `/coupons/${code}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Coupon"],
    }),
    validateCoupon: builder.mutation<
      ApiResponse<{ discountAmount: number; isFreeDelivery: boolean; coupon: Coupon }>,
      ValidateCouponRequest
    >({
      query: (data) => ({
        url: "/coupons/validate",
        method: "POST",
        body: data,
      }),
    }),
  }),
});

export const {
  useGetCouponsQuery,
  useGetCouponByCodeQuery,
  useCreateCouponMutation,
  useUpdateCouponMutation,
  useDeleteCouponMutation,
  useValidateCouponMutation,
} = couponsApi;
